import type { LinearEngineParams, SegmentDetail } from '@/types/price-structure';

import { AGGRESSIVENESS, GRANULARITY } from './constants';

export interface ExplainLine {
    label: string;
    formula: string;
    result: string;
}

export interface Explanation {
    /** Frase de uma linha: o que o motor decidiu e por quê. */
    verdict: string;
    /** A aritmética, passo a passo. */
    lines: ExplainLine[];
    /** Aviso quando a entrega ficou abaixo do pedido. */
    caveat: string | null;
}

const fmt = (value: number | null | undefined, digits = 2) =>
    value === null || value === undefined ? '—' : Number(value).toFixed(digits);

/**
 * Reconstrói o raciocínio do motor para um bloco.
 *
 * O objetivo é que os números não pareçam arbitrários: cada valor exibido é
 * derivado dos que vieram do GAM mais os dials do setup, e a conta aparece.
 */
export function explainSegment(
    segment: SegmentDetail,
    aggressiveness: number,
    granularity: number,
): Explanation {
    const profile = AGGRESSIVENESS.find((a) => a.value === aggressiveness);
    const gran = GRANULARITY.find((g) => g.value === granularity);

    const topMultiplier = profile?.topMultiplier ?? 1;
    const band = profile?.band ?? 1;
    const target = segment.pmr_target ?? 0;
    const pmr = segment.pmr ?? 0;
    const shrink = segment.shrink ?? 1;
    const steps = segment.steps ?? [];

    const lines: ExplainLine[] = [];

    lines.push({
        label: 'Base',
        formula: `eCPM do segmento nos últimos dias`,
        result: fmt(segment.ecpm),
    });

    const nominal = segment.top_nominal ?? (segment.ecpm ?? 0) * topMultiplier;
    lines.push({
        label: 'Topo nominal',
        formula: `${fmt(segment.ecpm)} × ${fmt(topMultiplier)} (agressividade ${aggressiveness})`,
        result: fmt(nominal),
    });

    lines.push({
        label: 'Ajuste de PMR',
        formula: `√(${fmt(pmr)}% ÷ ${fmt(target, 0)}%) = ${fmt(rawShrink(pmr, target), 3)}${
            clamped(pmr, target) ? ` → limitado a ${fmt(shrink, 3)}` : ''
        }`,
        result: `× ${fmt(shrink, 3)}`,
    });

    lines.push({
        label: 'Topo aplicado',
        formula: `${fmt(nominal)} × ${fmt(shrink, 3)}`,
        result: fmt(segment.anchor_top),
    });

    // Quando o piso é estendido, a conta da banda deixa de descrever o valor
    // final — mostrar "topo × banda" ali seria uma linha falsa.
    const extension = parseFloorExtension(segment.degradation_reason);
    if (extension) {
        lines.push({
            label: 'Piso da banda',
            formula: `${fmt(segment.anchor_top)} × ${fmt(band)} (banda da agressividade ${aggressiveness})`,
            result: fmt(extension.from),
        });
        lines.push({
            label: 'Piso estendido',
            formula: `banda rendia só ${extension.available} preços — piso baixado até caber uma cascata`,
            result: fmt(segment.anchor_bottom),
        });
    } else {
        lines.push({
            label: 'Piso',
            formula: `${fmt(segment.anchor_top)} × ${fmt(band)} (banda da agressividade ${aggressiveness})`,
            result: fmt(segment.anchor_bottom),
        });
    }

    if (steps.length > 1) {
        const gaps = steps.slice(0, -1).map((s, i) => s / steps[i + 1] - 1);
        lines.push({
            label: 'Distribuição',
            formula: `${steps.length} steps, saltos de ${fmt(Math.min(...gaps) * 100, 1)}% na base até ${fmt(
                Math.max(...gaps) * 100,
                1,
            )}% no topo (granularidade ${granularity})`,
            result: `${steps[0]} → ${steps[steps.length - 1]}`,
        });
    }

    return {
        verdict: buildVerdict(segment, pmr, target, shrink, gran?.short),
        lines,
        caveat: buildCaveat(segment),
    };
}

/**
 * A conta do motor v4 (engine/linear.py). Sem PMR nem curva: abertura, chão e
 * degraus iguais entre os dois, cada um subindo para o preço válido de cima.
 */
export function explainLinear(segment: SegmentDetail, params: LinearEngineParams): Explanation {
    const ecpm = segment.ecpm ?? 0;
    const opening = segment.top_nominal ?? ecpm * params.open_multiplier;
    const floor = segment.anchor_bottom ?? opening * params.floor_fraction;
    const steps = segment.steps ?? [];
    const pct = Math.round(params.floor_fraction * 100);

    const lines: ExplainLine[] = [
        { label: 'Base', formula: 'eCPM do segmento na janela', result: fmt(ecpm) },
        {
            label: 'Abertura',
            formula: `${fmt(ecpm)} × ${fmt(params.open_multiplier)}`,
            result: fmt(opening),
        },
        { label: 'Chão', formula: `${fmt(opening)} × ${pct}%`, result: fmt(floor) },
    ];

    if (steps.length) {
        lines.push({
            label: 'Degraus',
            formula: 'saltos iguais da abertura ao chão, cada um arredondado para cima na escada do GAM',
            result: steps.join(' → '),
        });
    }

    const verdict = steps.length
        ? `Abre em ${steps[0]} — ${fmt(params.open_multiplier)}× o eCPM de ${fmt(ecpm)} — e desce ` +
          `em degraus iguais até ${fmt(floor)}` +
          (params.fallback ? ', com uma última tentativa a 0,05.' : '.')
        : 'eCPM baixo demais: os degraus colapsam num preço só, então este segmento fica sem ' +
          'cascata própria e usa a do país ou do bloco.';

    return {
        verdict,
        lines,
        caveat: segment.steps?.length ? (segment.degradation_reason ?? null) : null,
    };
}

/** Extrai os números da mensagem de piso estendido gerada pelo motor. */
function parseFloorExtension(
    reason?: string | null,
): { from: number; available: number } | null {
    if (!reason?.startsWith('piso estendido')) return null;
    const from = reason.match(/de ([\d.]+) para/);
    const available = reason.match(/continha (\d+) preços/);
    if (!from) return null;
    return { from: parseFloat(from[1]), available: Number(available?.[1] ?? 0) };
}

function rawShrink(pmr: number, target: number) {
    return target > 0 ? Math.sqrt(pmr / target) : 1;
}

function clamped(pmr: number, target: number) {
    const raw = rawShrink(pmr, target);
    return raw < 0.6 || raw > 1.15;
}

function buildVerdict(
    segment: SegmentDetail,
    pmr: number,
    target: number,
    shrink: number,
    granLabel?: string,
): string {
    if (!segment.steps?.length) {
        return 'O motor não conseguiu montar uma cascata utilizável para este bloco.';
    }

    const extension = parseFloorExtension(segment.degradation_reason);
    if (extension) {
        return (
            `PMR de ${fmt(pmr)}% contra alvo de ${fmt(target, 0)}% deixou a banda estreita demais ` +
            `— só ${extension.available} preços válidos entre ${fmt(extension.from)} e ` +
            `${fmt(segment.anchor_top)}. O motor baixou o piso até ${fmt(segment.anchor_bottom)} ` +
            `para entregar uma cascata, em vez de deixar o bloco com os steps padrão do sistema.`
        );
    }

    const delta = pmr - target;
    const raw = rawShrink(pmr, target);

    if (raw >= 1.15) {
        return (
            `PMR de ${fmt(pmr)}% está muito acima do alvo de ${fmt(target, 0)}% — o segmento entrega ` +
            `com folga, então o motor esticou o topo até o limite permitido (+15%). ` +
            `Sem esse teto, a conta pediria ${fmt(raw, 2)}×.`
        );
    }
    if (raw <= 0.6) {
        return (
            `PMR de ${fmt(pmr)}% está muito abaixo do alvo de ${fmt(target, 0)}% — o segmento já está ` +
            `sufocado, então o motor recuou a cascata inteira ao mínimo permitido (−40%). ` +
            `Sem esse piso, a conta pediria ${fmt(raw, 2)}×.`
        );
    }
    if (delta > 0) {
        return (
            `PMR de ${fmt(pmr)}% está ${fmt(delta)} ponto(s) acima do alvo de ${fmt(target, 0)}% — ` +
            `há espaço para forçar, então o motor subiu o topo em ${fmt((shrink - 1) * 100, 1)}%.`
        );
    }
    if (delta < 0) {
        return (
            `PMR de ${fmt(pmr)}% está ${fmt(Math.abs(delta))} ponto(s) abaixo do alvo de ` +
            `${fmt(target, 0)}% — o motor recuou a cascata em ${fmt((1 - shrink) * 100, 1)}% ` +
            `para não sufocar ainda mais a entrega.`
        );
    }
    return `PMR está exatamente no alvo de ${fmt(target, 0)}% — o topo ficou no valor nominal${
        granLabel ? `, distribuído em granularidade ${granLabel}` : ''
    }.`;
}

function buildCaveat(segment: SegmentDetail): string | null {
    if (segment.degradation_reason) return segment.degradation_reason;

    const delivered = segment.steps_delivered ?? 0;
    const requested = segment.steps_requested ?? 0;
    if (requested > 0 && delivered < requested) {
        return (
            `Entregou ${delivered} dos ${requested} steps pedidos: nessa faixa de preço o ` +
            `vocabulário do GAM não tem mais valores distintos.`
        );
    }
    return null;
}
